import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useInteractiveCallback } from '@/hook/useInteractiveCallback';

type SwitchProps = {
    id?: string;
    disabled?: boolean;
    value: boolean;
    size?: 'small' | 'medium';
    onToggle: () => void;
};

export const Switch: React.FC<SwitchProps> = ({
    id,
    value,
    disabled,
    size = 'medium',
    onToggle,
}) => {
    const handleKeyDown = useInteractiveCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();

                handleOnToggle();
            }
        },
    );

    const handleOnToggle = useInteractiveCallback(() => {
        if (disabled) {
            return;
        }

        onToggle();
    });

    // NOTE: [Generated with ChatGPT]
    // The hidden checkbox ensures the switch component is more accessible and integrates well with
    //   existing accessibility tools. It doesn’t affect the visual styling or experience for most users
    //   but greatly enhances the usability for those relying on assistive technologies.
    //   The combination of role="switch", aria-checked, and the checkbox provides a comprehensive
    //   solution to make sure all users can easily interact with the component.

    return (
        <div
            className={classNames(
                'flex-shrink-0 aspect-[2.25] flex items-center rounded-full py-0.5 cursor-pointer transition duration-300 ease-in-out',
                {
                    'bg-brand-teal': value,
                    'bg-gray-40': !value,
                    'opacity-50': disabled,
                    'w-12 h-7': size === 'medium',
                    'w-10 h-6': size === 'small',
                },
            )}
            role="switch"
            aria-disabled={disabled}
            aria-checked={value}
            tabIndex={0}
            onClick={handleOnToggle}
            onKeyDown={handleKeyDown}>
            <input
                id={id}
                name={id}
                type="checkbox"
                checked={value}
                onChange={handleOnToggle}
                className="hidden"
                aria-hidden="true"
            />
            <div
                className={classNames(
                    'bg-white rounded-full shadow-medium transform transition duration-300 ease-in-out',
                    {
                        'translate-x-6': value && size === 'medium',
                        'translate-x-1':
                            !value && (size === 'medium' || size === 'small'),
                        'translate-x-5': value && size === 'small',
                        'w-5 h-5': size === 'medium',
                        'w-4 h-4': size === 'small',
                    },
                )}
            />
        </div>
    );
};
